import { useEffect, useState, type FormEvent } from 'react'
import { Link, useParams } from 'react-router-dom'
import { api, ApiError, createOrQueue } from '../api.ts'
import { relativeDay, dateTime } from '../format.ts'
import { EditClientDialog } from '../components/EditClientDialog.tsx'

interface Note {
  id: string
  body: string
  occurredAt: string
  createdAt: string
  authorCode: string | null
}
interface Client {
  id: string
  name: string
  type: string
  nip: string | null
  regon: string | null
  street: string | null
  postalCode: string | null
  city: string | null
  phone: string | null
  email: string | null
  machineryPark: string | null
  tags: string[]
  status: string
  /** the note timeline (the card's own free-text `notes` column is separate) */
  timeline: Note[]
  ownerCode: string | null
  ownerName: string | null
}

const TYPE_LABEL: Record<string, string> = {
  gospodarstwo: 'Gospodarstwo',
  firma: 'Firma',
  osoba_prywatna: 'Osoba prywatna',
}
const STATUS_STYLE: Record<string, string> = {
  aktywny: 'bg-green-100 text-green-700',
  potencjalny: 'bg-amber-100 text-amber-700',
  nieaktywny: 'bg-slate-100 text-slate-500',
}

/**
 * The client card — "jedno miejsce ze wszystkim" (SPEC 5.7). Today: details,
 * note timeline (contact persons, orchard area/crops and the administrative
 * address fields were dropped from the card — W68). Offers, machines owned, service tickets and tasks
 * appear as their stages land; they're announced rather than hidden so the
 * shape of the finished card is visible from the start.
 */
export function ClientPage() {
  const { id } = useParams<{ id: string }>()
  const [c, setC] = useState<Client | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [editing, setEditing] = useState(false)

  function reload() {
    if (!id) return
    api
      .get<{ client: Client }>(`/api/clients/${id}`)
      .then((r) => setC(r.client))
      .catch((e) => setError(e instanceof Error ? e.message : 'Błąd'))
  }
  useEffect(reload, [id])

  if (error) return <div className="p-6 text-red-700">{error}</div>
  if (!c) return <div className="p-6 text-slate-400">Ładowanie…</div>

  const address = [c.street, [c.postalCode, c.city].filter(Boolean).join(' ')].filter(Boolean).join(', ')

  return (
    <div className="p-6 max-w-5xl">
      <Link to="/klienci" className="text-sm text-slate-500 hover:text-slate-700">
        ← Klienci
      </Link>

      <div className="mt-2 mb-5 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-slate-800">{c.name}</h1>
          <div className="flex flex-wrap gap-2 mt-1.5 text-xs items-center">
            <span className={`px-2 py-0.5 rounded ${STATUS_STYLE[c.status] ?? ''}`}>{c.status}</span>
            <span className="bg-slate-100 text-slate-600 px-2 py-0.5 rounded">{TYPE_LABEL[c.type] ?? c.type}</span>
            {c.ownerCode && (
              <span className="text-slate-500">
                opiekun: <strong>{c.ownerCode}</strong>
                {c.ownerName && ` (${c.ownerName})`}
              </span>
            )}
            {c.tags.map((t) => (
              <span key={t} className="bg-brand-100 text-brand-700 px-2 py-0.5 rounded">
                {t}
              </span>
            ))}
          </div>
        </div>
        <button
          onClick={() => setEditing(true)}
          className="shrink-0 text-sm border border-slate-300 text-slate-700 px-3 py-1.5 rounded-md hover:border-brand-600 hover:text-brand-700"
        >
          Edytuj
        </button>
      </div>

      {editing && (
        <EditClientDialog
          client={c}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false)
            reload()
          }}
        />
      )}

      <div className="grid gap-4 md:grid-cols-3">
        {/* Left: details */}
        <div className="md:col-span-1 space-y-4">
          <section className="bg-white border border-slate-200 rounded-lg p-4">
            <h2 className="font-semibold text-slate-700 mb-2 text-sm">Dane</h2>
            <dl className="space-y-1.5 text-sm">
              <Row label="NIP" value={c.nip} mono />
              <Row label="REGON" value={c.regon} mono />
              <Row label="Telefon" value={c.phone} />
              <Row label="E-mail" value={c.email} />
              <Row label="Adres" value={address || null} />
            </dl>
            {c.machineryPark && (
              <div className="mt-2 pt-2 border-t border-slate-100">
                <div className="text-slate-500 text-sm mb-0.5">Park maszynowy</div>
                <div className="text-sm text-slate-700">{c.machineryPark}</div>
              </div>
            )}
          </section>
        </div>

        {/* Right: timeline + future sections */}
        <div className="md:col-span-2 space-y-4">
          <TimelineCard clientId={c.id} notes={c.timeline} onChanged={reload} />

          <CustomerAssets clientId={c.id} />
        </div>
      </div>
    </div>
  )
}

// ── Owned machines, service history, tasks (SPEC 5.7, Etap 7) ─────────────────

interface OwnedUnit {
  unitId: string
  model: string
  manufacturerName: string
  serialNumber: string | null
  soldAt: string | null
  warrantyEnd: string | null
  warrantyActive: boolean
}
interface CustTicket {
  id: string
  number: string
  serialNumber: string | null
  model: string | null
  serviceType: string
  status: string
  reportedAt: string
}
interface CustTask {
  id: string
  kind: string
  title: string
  dueAt: string | null
  status: string
}

function CustomerAssets({ clientId }: { clientId: string }) {
  const [units, setUnits] = useState<OwnedUnit[]>([])
  const [tickets, setTickets] = useState<CustTicket[]>([])
  const [tasks, setTasks] = useState<CustTask[]>([])

  useEffect(() => {
    api.get<{ units: OwnedUnit[] }>(`/api/clients/${clientId}/units`).then((r) => setUnits(r.units)).catch(() => {})
    api.get<{ tickets: CustTicket[] }>(`/api/clients/${clientId}/service`).then((r) => setTickets(r.tickets)).catch(() => {})
    api.get<{ tasks: CustTask[] }>(`/api/clients/${clientId}/tasks`).then((r) => setTasks(r.tasks)).catch(() => {})
  }, [clientId])

  return (
    <>
      <section className="bg-white border border-slate-200 rounded-lg p-4">
        <h2 className="font-semibold text-slate-700 mb-2 text-sm">Posiadane maszyny</h2>
        {units.length === 0 ? (
          <p className="text-sm text-slate-400">Brak wydanych maszyn.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {units.map((u) => (
              <li key={u.unitId} className="py-2 flex items-center justify-between gap-3 text-sm">
                <Link to={`/magazyn/${u.unitId}`} className="min-w-0">
                  <span className="text-slate-700">{u.model}</span>
                  <span className="block text-xs text-slate-400 font-mono">{u.serialNumber ?? '—'}</span>
                </Link>
                <span className="text-xs shrink-0">
                  {u.warrantyEnd ? (
                    <span className={u.warrantyActive ? 'text-green-700' : 'text-slate-400'}>
                      {u.warrantyActive ? `gwarancja do ${new Date(u.warrantyEnd).toLocaleDateString('pl-PL')}` : 'po gwarancji'}
                    </span>
                  ) : (
                    <span className="text-slate-300">bez gwarancji</span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="bg-white border border-slate-200 rounded-lg p-4">
        <h2 className="font-semibold text-slate-700 mb-2 text-sm">Historia serwisowa</h2>
        {tickets.length === 0 ? (
          <p className="text-sm text-slate-400">Brak zgłoszeń.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {tickets.map((t) => (
              <li key={t.id} className="py-1.5 text-sm flex justify-between gap-3">
                <Link to={`/serwis/${t.id}`} className="text-brand-700 hover:underline">
                  {t.number}
                </Link>
                <span className="text-xs text-slate-400">
                  {t.model ?? ''} · {t.status} · {new Date(t.reportedAt).toLocaleDateString('pl-PL')}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {tasks.length > 0 && (
        <section className="bg-white border border-slate-200 rounded-lg p-4">
          <h2 className="font-semibold text-slate-700 mb-2 text-sm">Zadania</h2>
          <ul className="space-y-1 text-sm">
            {tasks.map((t) => (
              <li key={t.id} className="text-slate-600">
                {t.title}
                {t.dueAt && <span className="text-xs text-slate-400"> · {new Date(t.dueAt).toLocaleDateString('pl-PL')}</span>}
              </li>
            ))}
          </ul>
        </section>
      )}
    </>
  )
}

function TimelineCard({ clientId, notes, onChanged }: { clientId: string; notes: Note[]; onChanged: () => void }) {
  const [body, setBody] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function add(e: FormEvent) {
    e.preventDefault()
    if (!body.trim()) return
    setBusy(true)
    setError(null)
    try {
      // occurredAt is stamped now, so a note written offline at 14:00 lands on the
      // timeline at 14:00 even if it syncs at 18:00 (blueprint §4.9).
      const r = await createOrQueue('note', `/api/clients/${clientId}/notes`, { body, occurredAt: new Date().toISOString() })
      setBody('')
      if (r.queued) setError('Zapisano lokalnie — notatka wyśle się, gdy wróci sieć.')
      else onChanged()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Nie udało się zapisać')
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="bg-white border border-slate-200 rounded-lg p-4">
      <h2 className="font-semibold text-slate-700 mb-3 text-sm">Oś czasu</h2>

      <form onSubmit={add} className="mb-4">
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={2}
          placeholder="Co ustaliliście? Notatka trafi na oś czasu…"
          className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:border-brand-600 resize-y"
        />
        {error && <div className="text-xs text-red-700 mt-1">{error}</div>}
        <button
          type="submit"
          disabled={busy || !body.trim()}
          className="mt-1.5 text-sm bg-brand-600 text-white px-3 py-1 rounded-md hover:bg-brand-700 disabled:opacity-50"
        >
          {busy ? 'Zapisuję…' : 'Dodaj notatkę'}
        </button>
      </form>

      {notes.length === 0 ? (
        <p className="text-sm text-slate-400">Jeszcze nic tu nie ma.</p>
      ) : (
        <ol className="space-y-3">
          {notes.map((n) => {
            // A note written in the orchard at 14:00 that synced at 18:00 sits
            // at 14:00 (blueprint §4.9). Flag the gap so it isn't confusing.
            const late = Math.abs(new Date(n.createdAt).getTime() - new Date(n.occurredAt).getTime()) > 3600_000
            return (
              <li key={n.id} className="border-l-2 border-brand-100 pl-3">
                <div className="flex items-baseline gap-2 text-xs text-slate-400">
                  <span className="font-medium text-slate-600">{relativeDay(n.occurredAt)}</span>
                  <span>{dateTime(n.occurredAt)}</span>
                  {n.authorCode && <span className="font-mono">{n.authorCode}</span>}
                  {late && <span title={`Zapisano ${dateTime(n.createdAt)}`}>· dosłane później</span>}
                </div>
                <p className="text-sm text-slate-700 mt-0.5 whitespace-pre-wrap">{n.body}</p>
              </li>
            )
          })}
        </ol>
      )}
    </section>
  )
}

function Row({ label, value, mono }: { label: string; value: string | null; mono?: boolean }) {
  return (
    <li className="flex justify-between gap-3 list-none">
      <dt className="text-slate-500 shrink-0">{label}</dt>
      <dd className={`text-right text-slate-700 ${mono ? 'font-mono text-xs' : ''}`}>{value ?? '—'}</dd>
    </li>
  )
}
